import { IFilterAngularComp } from '@ag-grid-community/angular';
import { IDoesFilterPassParams, IFilterParams } from '@ag-grid-community/core';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ListFilterPipe } from '@app/core/components/list/pipes/list-filter.pipe';
import { GridComponent } from '../../grid.component';
import { GridColumn } from '../../grid.types';
import { GridService } from '../../grid.service';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormControl } from '@angular/forms';
import { debounceTime, switchMap, of, Observable, mergeMap, BehaviorSubject } from 'rxjs';

@Component({
    selector: 'tt-chip-filter',
    templateUrl: './chip-filter.component.html',
    styleUrls: ['./chip-filter.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class ChipFilterComponent implements IFilterAngularComp, OnDestroy {
    public filterParams!: IFilterParams;

    /**
     * Element reference to the input element.
     */
    @ViewChild('inputRef')
    public inputRef?: ElementRef;

    /**
     * Form-control for the search field.
     */
    public searchControl = new FormControl('');

    constructor(private gridService: GridService) {}

    public data?: Observable<string[]>;

    public selectedNames$ = new BehaviorSubject<string[]>([]);

    public selectedNames: string[] = [];

    private getData(search: string | null) {
        const overlay = this.getCdkOverlay();
        if (!!overlay) overlay.style.zIndex = '10000';
        const data = new Set<string>();

        this.filterParams.api.forEachNode((node) => {
            if (!this.filterParams.colDef.colId) return;

            const array = node.data[this.filterParams.colDef.colId];

            if (Array.isArray(array)) {
                array.forEach((item) => data.add(item.item_name));
            }
        });

        return Array.from(data).filter((item) => !this.selectedNames.includes(item) && ('' + item).toLowerCase().includes(search?.toLowerCase() ?? ''));
    }

    public remove(name: string, _: MatAutocomplete) {
        _.showPanel = false;
        const index = this.selectedNames.findIndex((n) => name === n);

        if (index > -1) {
            this.selectedNames.splice(index, 1);
            this.searchControl.setValue('');

            this.selectedNames$.next(this.selectedNames);
        }
    }

    /**
     * Adds a chip from selection in autocomplete.
     *
     * @param event the autocomplete selection event.
     */
    public selected(event: MatAutocompleteSelectedEvent): void {
        this.selectedNames.push(event.option.value);
        this.selectedNames$.next(this.selectedNames);

        if (!!this.inputRef) this.inputRef.nativeElement.value = '';

        this.searchControl.setValue(null);
    }

    onNewRowsLoaded(): void {
        this.searchControl.setValue(this.searchControl.value);
    }

    public clearFilterText = '';

    agInit(params: IFilterParams & { grid: GridComponent; ttColumn: GridColumn }): void {
        this.clearFilterText = params.grid.localeText.clearFilter;
        // params.api.
        this.filterParams = params;

        this.data = this.searchControl.valueChanges.pipe(
            debounceTime(250),
            switchMap((value) => of(this.getData(value))),
        );

        this.selectedNames$.subscribe({
            next: (value) => {
                this.filterParams.filterChangedCallback();
            },
        });
    }

    clearFilter() {
        this.selectedNames = [];
        this.selectedNames$.next(this.selectedNames);
        if (!!this.inputRef) this.inputRef.nativeElement.value = '';

        this.searchControl.setValue('');
    }

    doesFilterPass(params: IDoesFilterPassParams): boolean {
        if (!this.isFilterActive()) return true;

        const column = this.filterParams.colDef.colId;

        if (!!column) {
            const chips = params.data[column];

            if (Array.isArray(chips)) {
                return chips.some((item) => this.selectedNames.includes(item.item_name));
            }
        }

        return false;
    }

    isFilterActive() {
        return this.selectedNames.length > 0;
    }

    getModel() {
        return this.isFilterActive() ? this.selectedNames : null;
    }

    setModel(model: any) {
        if (Array.isArray(model) && model.every((item) => typeof item === 'string')) {
            this.selectedNames = model;
            this.filterParams.filterChangedCallback();
        }
    }

    ngOnDestroy(): void {
        const overlay = this.getCdkOverlay();

        if (!!overlay) overlay.style.zIndex = '';
    }

    private getCdkOverlay() {
        const container: HTMLDivElement | undefined = document.body.querySelector('.cdk-overlay-container') as HTMLDivElement | undefined;
        return container;
    }
}
